import React, { useEffect, useMemo, useRef, useState } from 'react';
import './PrintModal.css';

import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';

import { useTranslation } from 'react-i18next';

import ScaleLine from 'ol/control/ScaleLine';

const PREVIEW_PLACEHOLDER =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
      <rect width="100%" height="100%" fill="#ffffff"/>
      <g fill="#000000">
        <rect x="350" y="200" rx="80" ry="80" width="500" height="400"/>
        <rect x="450" y="260" width="500" height="400" rx="80" ry="80"/>
        <path d="M520 560 L630 420 L690 480 L760 380 L880 560 Z" fill="#ffffff"/>
        <circle cx="820" cy="340" r="30" fill="#ffffff"/>
      </g>
    </svg>
  `);

const PAPER_MM = {
  'A4-P': { w: 210, h: 297, label: 'A4 Retrato' },
  'A4-L': { w: 297, h: 210, label: 'A4 Paisagem' },
  'A3-P': { w: 297, h: 420, label: 'A3 Retrato' },
  'A3-L': { w: 420, h: 297, label: 'A3 Paisagem' },
};

const CONFIGS = {
  default: {
    label: 'Padrão',
    marginsMm: { top: 15, right: 15, bottom: 18, left: 15 },
    showWatermark: true,
    watermarkSrc: '/portal/logo-pedea-wm.png',
  },
  idesp: {
    label: 'IDESP',
    marginsMm: { top: 12, right: 12, bottom: 20, left: 12 },
    showWatermark: true,
    watermarkSrc: '/portal/logo-pedea-wm.png',
  },
};

const DPI_OPTIONS = [
  { label: '96 dpi', value: 96 },
  { label: '150 dpi', value: 150 },
  { label: '254 dpi', value: 254 },
  { label: '300 dpi', value: 300 },
];

const PAPER_OPTIONS = Object.entries(PAPER_MM).map(([value, paper]) => ({
  label: paper.label,
  value,
}));

const mmToPx = (mm, dpi) => Math.round((mm / 25.4) * dpi);

function getActiveLayers(map) {
  const layers = map.getLayers().getArray();

  return layers
    .filter((layer) => layer.getVisible())
    .map((l) => ({
      title: l.get('title'),
      name: l.get('name'),
      wmsName: l.get('wmsName'),
      serverType: l.get('serverType'),
    }))
    .filter((l) => typeof l.title === 'string' && l.title.trim() !== '')
    .filter((l) => l.title.toLowerCase() !== 'csv')
    .filter((l) => l.serverType !== 'esri' && l.title.toLowerCase() !== 'mapa topográfico')
    .map((l) => l.title.trim())
    .sort((a, b) => a.localeCompare(b))
    .join(', ');
}

function drawScale(ctx, map, paperHpx, marginPx) {
  const scaleLine = new ScaleLine();
  scaleLine.setMap(map);

  const el = scaleLine.element;
  el.style.position = 'absolute';
  el.style.top = '-1000px';
  document.body.appendChild(el);

  map.renderSync();

  const txt = el.innerText;
  document.body.removeChild(el);

  const scaleText = `Escala: ${txt}`;

  ctx.font = 'bold 20px Arial';
  ctx.fillStyle = '#000';
  ctx.fillText(scaleText, marginPx, paperHpx - marginPx);
}

function drawNorth(ctx, paperWpx, topPx) {
  const img = new Image();

  return new Promise((resolve) => {
    img.onload = () => {
      const size = 28;
      ctx.drawImage(img, paperWpx - size - topPx, topPx, size, size);
      resolve();
    };

    img.onerror = () => resolve();

    img.src = '/portal/north-arrow.png';
  });
}

function drawWatermark(ctx, src, x, y, w) {
  const img = new Image();
  // Necessário para não "contaminar" o canvas quando a imagem vem de outra
  // origem (ex.: CDN ou outro host). O servidor precisa responder com
  // Access-Control-Allow-Origin para essa origem, senão o toDataURL()
  // do canvas final falha silenciosamente.
  img.crossOrigin = 'anonymous';
  img.src = src;

  return new Promise((resolve) => {
    img.onload = () => {
      const ratio = img.height / img.width;
      ctx.drawImage(img, x, y, w, w * ratio);
      resolve();
    };

    img.onerror = () => resolve();
  });
}

export default function PrintModal({ active, callback, map }) {
  const { t } = useTranslation();

  const [previewSrc, setPreviewSrc] = useState(PREVIEW_PLACEHOLDER);
  const [show, setShow] = useState(false);
  const [dpi, setDpi] = useState(254);
  const [config, setConfig] = useState('idesp');
  const [paper, setPaper] = useState('A4-L');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [author, setAuthor] = useState('');
  const [withLegend, setWithLegend] = useState(false);
  const [previewError, setPreviewError] = useState(false);

  const previewRef = useRef(null);
  const working = useRef(false);

  useEffect(() => {
    if (active) {
      setShow(true);
    }
  }, [active]);

  useEffect(() => {
    callback(show);
  }, [show, callback]);

  const paperPx = useMemo(() => {
    const p = PAPER_MM[paper];

    return {
      w: mmToPx(p.w, dpi),
      h: mmToPx(p.h, dpi),
    };
  }, [paper, dpi]);

  const marginsPx = useMemo(() => {
    const m = CONFIGS[config]?.marginsMm || CONFIGS.default.marginsMm;

    return {
      top: mmToPx(m.top, dpi),
      right: mmToPx(m.right, dpi),
      bottom: mmToPx(m.bottom, dpi),
      left: mmToPx(m.left, dpi),
    };
  }, [config, dpi]);

  const mapBox = useMemo(() => {
    const titleBlock = mmToPx(20, dpi);
    const footerBlock = mmToPx(22, dpi);

    return {
      x: marginsPx.left,
      y: marginsPx.top + titleBlock,
      w: paperPx.w - marginsPx.left - marginsPx.right,
      h: paperPx.h - marginsPx.top - marginsPx.bottom - titleBlock - footerBlock,
    };
  }, [paperPx, marginsPx, dpi]);

  function waitMapReady(mapInstance) {
    return new Promise((resolve) => {
      const hasRenderer = !!mapInstance.getRenderer?.();
      const size = mapInstance.getSize?.() || [0, 0];
      const hasTarget = !!mapInstance.getTargetElement?.();

      if (hasRenderer && hasTarget && size[0] > 0 && size[1] > 0) {
        mapInstance.renderSync();
        resolve();
        return;
      }

      const done = () => {
        mapInstance.un('rendercomplete', done);
        resolve();
      };

      mapInstance.once('rendercomplete', done);

      try {
        mapInstance.updateSize?.();
      } catch (_) {
        // ignora — updateSize é best-effort aqui
      }

      mapInstance.render();
    });
  }

  async function makeImage() {
    if (!map || working.current) {
      return null;
    }

    working.current = true;
    setPreviewError(false);

    try {
      await waitMapReady(map);
      map.renderSync();

      const screenCanvas = document.createElement('canvas');
      const size = map.getSize();
      screenCanvas.width = size[0];
      screenCanvas.height = size[1];

      const sctx = screenCanvas.getContext('2d');

      Array.prototype.forEach.call(document.querySelectorAll('.ol-layer canvas'), (c) => {
        if (c.width > 0) {
          const opacity = c.parentNode.style.opacity;
          sctx.globalAlpha = opacity === '' ? 1 : Number(opacity);

          const transform = c.style.transform;
          const matrix = transform
            .match(/^matrix\(([^)]*)\)$/)
            ?.[1]
            ?.split(',')
            .map(Number);

          if (matrix) {
            CanvasRenderingContext2D.prototype.setTransform.apply(sctx, matrix);
          }

          sctx.drawImage(c, 0, 0);
        }
      });

      const out = document.createElement('canvas');
      out.width = paperPx.w;
      out.height = paperPx.h;

      const ctx = out.getContext('2d');

      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, out.width, out.height);

      ctx.fillStyle = '#000';
      ctx.font = 'bold 20px Arial';
      ctx.fillText(title || '', marginsPx.left, marginsPx.top + mmToPx(6, dpi));

      ctx.font = '14px Arial';

      if (subtitle) {
        ctx.fillText(subtitle, marginsPx.left, marginsPx.top + mmToPx(12, dpi));
      }

      if (author) {
        ctx.fillText(author, marginsPx.left, marginsPx.top + mmToPx(18, dpi));
      }

      const scale = Math.min(
        mapBox.w / screenCanvas.width,
        mapBox.h / screenCanvas.height
      );

      const mapDrawW = Math.round(screenCanvas.width * scale);
      const mapDrawH = Math.round(screenCanvas.height * scale);
      const mapDrawX = mapBox.x + (mapBox.w - mapDrawW) / 2;
      const mapDrawY = mapBox.y + (mapBox.h - mapDrawH) / 2;

      ctx.drawImage(screenCanvas, mapDrawX, mapDrawY, mapDrawW, mapDrawH);

      const conf = CONFIGS[config] || CONFIGS.default;

      if (conf.showWatermark && conf.watermarkSrc) {
        await drawWatermark(
          ctx,
          conf.watermarkSrc,
          marginsPx.left,
          marginsPx.top - mmToPx(8, dpi),
          mmToPx(45, dpi)
        );
      }

      await drawNorth(ctx, out.width, mmToPx(6, dpi));

      drawScale(ctx, map, out.height, marginsPx.left);

      const footerY = out.height - marginsPx.bottom + mmToPx(4, dpi);
      const layersTxt = getActiveLayers(map);

      ctx.font = 'bold 20px Arial';
      ctx.fillStyle = '#000';

      const fonte = 'Fonte: PEDEA CE/SEMA';
      ctx.fillText(`${fonte} | ${layersTxt}`, marginsPx.left, footerY);

      if (withLegend) {
        const legendW = mmToPx(60, dpi);
        const legendH = mmToPx(50, dpi);
        const lx = out.width - marginsPx.right - legendW;
        const ly = footerY - legendH - mmToPx(6, dpi);

        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 1;
        ctx.fillRect(lx, ly, legendW, legendH);
        ctx.strokeRect(lx, ly, legendW, legendH);

        ctx.fillStyle = '#000';
        ctx.font = 'bold 14px Arial';
        ctx.fillText('Legenda', lx + mmToPx(4, dpi), ly + mmToPx(8, dpi));

        ctx.font = '14px Arial';

        const lines = (layersTxt || '').split(', ').slice(0, 6);

        lines.forEach((L, i) => {
          ctx.fillText(`• ${L}`, lx + mmToPx(4, dpi), ly + mmToPx(14 + i * 5, dpi));
        });
      }

      // Ponto crítico: se alguma layer desenhada acima veio de origem
      // cross-origin sem CORS habilitado (ex.: tiles/WMS do GeoServer sem
      // header Access-Control-Allow-Origin e sem crossOrigin configurado
      // na Source do OpenLayers), o canvas fica "tainted" e toDataURL()
      // lança SecurityError. É isso que fazia a prévia ficar travada no
      // ícone placeholder, sem nenhum aviso visível.
      const dataUrl = out.toDataURL('image/png');

      setPreviewSrc(dataUrl);

      if (previewRef.current) {
        previewRef.current.src = dataUrl;
      }

      return dataUrl;
    } catch (err) {
      console.error('Falha ao gerar a imagem de impressão do mapa:', err);
      setPreviewError(true);
      return null;
    } finally {
      working.current = false;
    }
  }

  async function handlePrint() {
    const dataUrl = await makeImage();

    if (!dataUrl) {
      return;
    }

    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `mapa_${paper}_${dpi}dpi.png`;
    a.click();
  }

  useEffect(() => {
    if (!show) {
      return;
    }

    makeImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, dpi, config, paper, title, subtitle, author, withLegend]);

  const dialogFooter = (
    <div className="PrintModalFooter">
      <Button
        type="button"
        severity="secondary"
        raised
        icon="pi pi-times"
        className="CloseBtn"
        onClick={() => setShow(false)}
      >
        {t('common.close') || 'Fechar'}
      </Button>

      <Button raised icon="pi pi-print" className="PrintBtn" onClick={handlePrint}>
        {t('print.print') || 'Imprimir'}
      </Button>
    </div>
  );

  return (
    <Dialog
      visible={show}
      onHide={() => setShow(false)}
      header={t('print.title') || 'Imprimir'}
      className="PrintModal"
      modal
      draggable={false}
      resizable={false}
      dismissableMask={false}
      closable
      footer={dialogFooter}
      style={{ borderRadius: '18px' }}
    >
      <div className="ContainerPrintWindow">
        <div className="PrintPreviewColumn">
          <img
            ref={previewRef}
            alt="preview"
            src={previewSrc}
            className="preview-img"
            onError={(e) => {
              e.currentTarget.src = PREVIEW_PLACEHOLDER;
            }}
          />

          {previewError && (
            <div className="PrintPreviewError">
              {t('print.previewError') ||
                'Não foi possível gerar a prévia do mapa. Verifique o CORS das camadas do GeoServer.'}
            </div>
          )}
        </div>

        <div className="PrintFormColumn">
          <div className="PrintFormGroup">
            <label htmlFor="print-dpi">Resolução</label>

            <Dropdown
              id="print-dpi"
              value={dpi}
              options={DPI_OPTIONS}
              onChange={(e) => setDpi(e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="Selecione a resolução"
              className="PrintDropdown"
              panelClassName="PrintDropdownPanel"
            />
          </div>

          <div className="PrintFormGroup">
            <label htmlFor="print-paper">Tamanho da folha</label>

            <Dropdown
              id="print-paper"
              value={paper}
              options={PAPER_OPTIONS}
              onChange={(e) => setPaper(e.value)}
              optionLabel="label"
              optionValue="value"
              placeholder="Selecione o tamanho"
              className="PrintDropdown"
              panelClassName="PrintDropdownPanel"
            />
          </div>

          <div className="PrintFormGroup">
            <label htmlFor="print-title">Título</label>

            <InputText
              id="print-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="PrintInput"
              placeholder="Digite o título"
            />
          </div>

          <div className="PrintFormGroup">
            <label htmlFor="print-subtitle">Subtítulo</label>

            <InputText
              id="print-subtitle"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              className="PrintInput"
              placeholder="Digite o subtítulo"
            />
          </div>

          <div className="PrintFormGroup">
            <label htmlFor="print-author">Autor</label>

            <InputText
              id="print-author"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="PrintInput"
              placeholder="Digite o autor"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
}